/*
 * File: productDrawer.tsx
 * Project: meki
 * File Created: Monday, 12th July 2021 1:09:57 pm
 * Author: Luis Aparicio (luis@inventures.cl)
 * -----
 * Last Modified: Monday, 20th November 2023 12:16:37 pm
 * Modified By: Blanca Munizaga (blanca@inventures.cl)
 * -----
 * Copyright 2019 - 2021 Incrementa Ventures SpA. ALL RIGHTS RESERVED
 * Terms and conditions defined in license.txt
 * -----
 * Inventures - www.inventures.cl
 */
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  IconButton,
  Typography,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemText,
  Divider,
  Checkbox,
  ListItemIcon,
  ListItemSecondaryAction,
  Badge,
  Collapse,
  Palette,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { BrandsInfo, ProductConnection } from '@interfaces';
import { UrlObject } from 'url';
import { useRouter } from 'next/router';
import { ExpandMore } from '@mui/icons-material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import { NEXT_PUBLIC_NO_PRICE_FALLBACK } from '@config/environment';
import { removeUndefineds } from '@utils';
import { styled } from '@mui/system';

interface productDrawerProps {
  handleToggleDrawer: () => void;
  toggleDrawer: boolean;
  data: ProductConnection;
  handleTotalFiltersChecked: (total: number) => void;
  setNavigating: (value: boolean) => void;
}

interface BrandChecked {
  checkState: boolean;
  brand: string;
  quantity: number;
}

interface PriceChecked {
  start?: number;
  stop?: number;
  checked: boolean;
  index: number;
}

const range = (props: {
  start: number;
  stop: number;
  step: number;
}): number[] =>
  Array.from(
    { length: (props.stop - props.start) / props.step + 1 },
    (_, i) => props.start + i * props.step,
  );

const createPriceRanges = (
  pricesRanges: number[],
): { start?: number; stop?: number; checked: boolean; index: number }[] => {
  return pricesRanges.reduce(
    (prev, current, i, array) => {
      if (i == pricesRanges.indexOf(pricesRanges[0])) {
        return [{ start: current, stop: undefined, checked: false, index: i }];
      }
      if (i == pricesRanges.indexOf(pricesRanges[pricesRanges.length - 1])) {
        return [
          ...prev,
          { start: undefined, stop: current, checked: false, index: i },
        ];
      }
      return [
        ...prev,
        { start: array[i - 1], stop: array[i], checked: false, index: i },
      ];
    },
    [{}],
  ) as { start?: number; stop?: number; checked: boolean; index: number }[];
};

const ListItemTextPrimaryStyled = styled(ListItemText)({
  '& .MuiListItemText-primary': {
    fontSize: 16,
    '&span': {
      fontSize: 16,
    },
  },
});
const NestedListItem = styled(ListItem)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
}));
const ListItemSecondaryActionStyled = styled(ListItemSecondaryAction)({
  fontSize: 16,
  '&span': {
    fontSize: 16,
  },
});
const DrawerStyled = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: 340,
    '@media (min-width: 600px)': {
      width: 340,
    },
  },
});
const AppBarStyled = styled(AppBar)({
  top: 0,
  bottom: 'auto',
});
const ToolbarStyled = styled(Toolbar)({
  justifyContent: 'space-between',
  minHeight: 64,
});
const TitleTypography = styled(Typography)({
  flexGrow: 1,
  paddingLeft: 13,
});
const OptionList = styled(List)({
  paddingTop: 0,
  paddingBottom: 60,
});
const FilterButtonDivAppBar = styled(AppBar)(({ theme }) => ({
  top: 'auto',
  bottom: 0,
  left: 0,
  right: 'auto',
  width: 'inherit',
  backgroundColor: (theme.palette as Palette).common.white,
}));
const FilterButtonDivToolbar = styled(Toolbar)({
  justifyContent: 'space-evenly',
});
const AplicationButton = styled(Button)({
  height: 32,
  '& .MuiButton-Text': {
    width: 94,
    height: 24,
  },
});
const ListItemTextTitleStyled = styled(ListItemText)({
  '& .MuiListItemText-primary': {
    fontFamily: 'Poppins',
    fontSize: 16,
    fontWeight: 'normal',
    fontStretch: 'normal',
    lineHeight: 'normal',
    letterSpacing: 0.16,
  },
});
const BrandListBadge = styled(Badge)({
  marginRight: 21,
});
function PriceStepItems(props: {
  rangesDuos: {
    start?: number;
    stop?: number;
    checked: boolean;
    index: number;
  }[];
  handleRangeSelect: (range: {
    start?: number;
    stop?: number;
    checked: boolean;
  }) => void;
}) {
  const { handleRangeSelect, rangesDuos } = props;
  const currFormat = new CurrencyFormatter({
    fallback: NEXT_PUBLIC_NO_PRICE_FALLBACK,
  });
  return (
    <>
      {rangesDuos.map((range, i) => {
        const labelId = `checkbox-list-price-label-${i}`;

        return (
          <Fragment key={i}>
            <ListItem
              role={undefined}
              dense
              button
              onClick={() => {
                handleRangeSelect(range);
              }}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={range.checked}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                  color={'primary'}
                />
              </ListItemIcon>
              <ListItemTextPrimaryStyled
                id={labelId}
                primary={
                  range.start && range.stop
                    ? `${currFormat.format(range.start)} - ${currFormat.format(
                        range.stop,
                      )}`
                    : range.start && !range.stop
                    ? `Menos de ${currFormat.format(range.start)}`
                    : !range.start && range.stop
                    ? `Más de ${currFormat.format(range.stop)}`
                    : 'No Ranges'
                }
              />
            </ListItem>
            <Divider component="li" />
          </Fragment>
        );
      })}
    </>
  );
}

function BrandsItems(props: {
  handleBrandItemClick: (brandName: string) => void;
  brandsList: { checkState: boolean; brand: string; quantity: number }[];
  brandsCheckList: { checkState: boolean; brand: string; quantity: number }[];
}) {
  const { handleBrandItemClick, brandsList, brandsCheckList } = props;

  return (
    <>
      {brandsList.map((brandInfo, i) => {
        const labelId = `checkbox-list-brand-label-${i}`;

        return (
          <Fragment key={i}>
            <NestedListItem
              role={undefined}
              dense
              onClick={() => {
                handleBrandItemClick(brandInfo.brand);
              }}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={
                    brandsCheckList.find((b) => b.brand === brandInfo.brand)
                      ?.checkState
                  }
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                  color={'primary'}
                />
              </ListItemIcon>
              <ListItemTextPrimaryStyled
                id={labelId}
                primary={`${brandInfo.brand}`}
              />
              <ListItemSecondaryActionStyled>
                {`(${brandInfo.quantity})`}
              </ListItemSecondaryActionStyled>
            </NestedListItem>
            <Divider component="li" />
          </Fragment>
        );
      })}
    </>
  );
}

export function ProductDrawer(props: productDrawerProps) {
  const {
    handleToggleDrawer,
    toggleDrawer,
    data,
    handleTotalFiltersChecked,
    setNavigating,
  } = props;
  const router = useRouter();
  const [openByBrand, setOpenByBrand] = useState(false);
  const [openByPrice, setOpenByPrice] = useState(false);

  const ranges = range({ start: 5000, stop: 30000, step: 5000 });
  const pricesRanges = createPriceRanges(ranges);

  const { current: INITIAL_STATE_RANGES } = useRef(pricesRanges);

  const [stateRanges, setStateRanges] =
    useState<PriceChecked[]>(INITIAL_STATE_RANGES);

  const handleOnRangeClick = useCallback(
    (rangeClicked: {
      start?: number;
      stop?: number;
      checked: boolean;
      index: number;
    }) => {
      const updatedRangesState = stateRanges.map((range) =>
        range.index === rangeClicked.index
          ? { ...range, checked: !range.checked }
          : range,
      );
      setStateRanges(updatedRangesState);
    },
    [stateRanges],
  );

  const { current: INITIAL_STATE_BRANDS } = useRef(
    data.brands.map((brand: BrandsInfo) => {
      return { ...brand, checkState: false };
    }),
  );

  const [brandsCheckList, setBrandsCheckList] =
    useState<BrandChecked[]>(INITIAL_STATE_BRANDS);

  const checkedItems = useMemo(() => {
    return {
      brands: brandsCheckList?.filter(
        (brandSelected) => brandSelected.checkState == true,
      ),
      priceRanges: stateRanges?.filter((range) => range.checked == true),
    };
  }, [brandsCheckList, stateRanges]);

  const handleBrandItemClick = useCallback(
    (brandName: string) => {
      const updateBrandItem = brandsCheckList.map((brandItem) =>
        brandItem.brand === brandName
          ? { ...brandItem, checkState: !brandItem.checkState }
          : brandItem,
      );
      setBrandsCheckList(updateBrandItem);
    },
    [brandsCheckList],
  );

  const handleCleanFilter = useCallback(async () => {
    setStateRanges(INITIAL_STATE_RANGES);
    setBrandsCheckList(INITIAL_STATE_BRANDS);

    const newRoute = (): UrlObject => ({
      pathname: router.pathname,
      query: removeUndefineds({
        ...router.query,
        brands: undefined,
        priceRanges: undefined,
        sortBy: router.query.sortBy,
      }),
    });
    handleToggleDrawer();
    setNavigating(true);
    await router.push(newRoute());
    setNavigating(false);
  }, [
    INITIAL_STATE_RANGES,
    INITIAL_STATE_BRANDS,
    router,
    handleToggleDrawer,
    setNavigating,
  ]);

  const handleSubmit = useCallback(async () => {
    const brands =
      checkedItems.brands.length > 0
        ? checkedItems.brands.map((brand: BrandChecked) => brand.brand).join()
        : undefined;
    const priceRanges =
      checkedItems.priceRanges.length > 0
        ? checkedItems.priceRanges
        : undefined;

    const newRoute = (
      brands: string,
      priceRanges: {
        start?: number;
        stop?: number;
        checked: boolean;
        index: number;
      }[],
    ): UrlObject => ({
      pathname: router.pathname,
      query: {
        ...router.query,
        brands: brands ?? brands,
        priceRanges: priceRanges?.map((range) => {
          if (!range.stop && !range.start) {
            return '';
          } else if (!range.start && range.stop) {
            return `stop:${range.stop}`;
          } else if (range.start && !range.stop) {
            return `start:${range.start}`;
          }
          return `start:${range.start};stop:${range.stop}`;
        }),
      },
    });
    handleToggleDrawer();
    setNavigating(true);
    await router.push(newRoute(brands, priceRanges));
    setNavigating(false);
  }, [handleToggleDrawer, router, checkedItems, setNavigating]);

  const handleClickByBrand = () => {
    setOpenByBrand(!openByBrand);
  };

  const handleClickByPrice = () => {
    setOpenByPrice(!openByPrice);
  };

  useEffect(
    function sumAllCheckFilter() {
      const totalChecks =
        checkedItems?.brands?.length + checkedItems?.priceRanges?.length;
      handleTotalFiltersChecked(totalChecks ?? 0);
    },
    [checkedItems, handleTotalFiltersChecked],
  );

  useEffect(
    function checkQueryParams() {
      const brandsQuery = (router.query.brands as string)?.split(',');
      const updateBrandItem = brandsCheckList.map((brandItem) =>
        brandsQuery?.find((brandQuery) => brandItem.brand === brandQuery)
          ? { ...brandItem, checkState: true }
          : brandItem,
      );
      setBrandsCheckList(updateBrandItem);
      if (router.query.priceRanges) {
        const queryRanges = [router.query.priceRanges]
          .flat()
          .map((priceRange) => {
            const queryRange = Object.fromEntries(
              priceRange.split(';').map((range) => range.split(':')),
            ) as { start?: string; stop?: string };

            return {
              start: +queryRange?.start ? +queryRange.start : undefined,
              stop: +queryRange?.stop ? +queryRange.stop : undefined,
            };
          });
        const updatedRangesState = stateRanges.map((range) =>
          queryRanges?.find(
            (queryRange) =>
              range.start === queryRange?.start &&
              range.stop === queryRange?.stop,
          )
            ? { ...range, checked: true }
            : range,
        );

        setStateRanges(updatedRangesState);
      }
    },
    [router.query],
  );

  return (
    <DrawerStyled anchor={'left'} open={toggleDrawer} onClose={handleSubmit}>
      <AppBarStyled position="sticky">
        <ToolbarStyled>
          <IconButton
            onClick={handleToggleDrawer}
            edge="start"
            color="inherit"
            aria-label="back"
            size="large"
          >
            <ArrowBackIcon />
          </IconButton>
          <TitleTypography variant="h6">Filtrar</TitleTypography>

          <IconButton
            onClick={handleToggleDrawer}
            edge="end"
            color="inherit"
            aria-label="back"
            size="large"
          >
            <CloseRoundedIcon />
          </IconButton>
        </ToolbarStyled>
      </AppBarStyled>
      <OptionList aria-label="options">
        <ListItem button onClick={handleClickByBrand}>
          <ListItemTextTitleStyled primary="Por marca" />
          <BrandListBadge
            badgeContent={checkedItems.brands?.length}
            hidden={checkedItems.brands?.length != 0}
            color="primary"
          ></BrandListBadge>
          {openByBrand ? <ExpandMore /> : <ChevronRightRoundedIcon />}
        </ListItem>
        <Collapse in={openByBrand} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <BrandsItems
              brandsCheckList={brandsCheckList}
              brandsList={brandsCheckList}
              handleBrandItemClick={handleBrandItemClick}
            />
          </List>
        </Collapse>
        <Divider component="li" />
        <ListItem button onClick={handleClickByPrice}>
          <ListItemTextTitleStyled primary="Por precio" />
          <BrandListBadge
            badgeContent={checkedItems.priceRanges?.length}
            hidden={checkedItems.priceRanges?.length != 0}
            color="primary"
          ></BrandListBadge>
          {openByPrice ? <ExpandMore /> : <ChevronRightRoundedIcon />}
        </ListItem>
        <Collapse in={openByPrice} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <PriceStepItems
              rangesDuos={stateRanges}
              handleRangeSelect={handleOnRangeClick}
            />
          </List>
        </Collapse>
        <Divider component="li" />
      </OptionList>
      <FilterButtonDivAppBar elevation={4}>
        <FilterButtonDivToolbar>
          <div>
            <Button
              color="primary"
              onClick={handleCleanFilter}
              variant={'text'}
            >
              Limpiar
            </Button>
          </div>
          <div>
            <AplicationButton
              disabled={
                checkedItems.brands.length > 0 ||
                checkedItems.priceRanges.length > 0
                  ? false
                  : true
              }
              color="primary"
              variant="contained"
              onClick={handleSubmit}
            >
              Aplicar
            </AplicationButton>
          </div>
        </FilterButtonDivToolbar>
      </FilterButtonDivAppBar>
    </DrawerStyled>
  );
}
